import { useEffect, useState } from "react";
import { BiggerSubscriptionDetail } from "../../interfaces/subscriptionDetail";
import { getVendors } from "../../services/vendorService";
import { Vendor } from "../../interfaces/vendor";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../services/helpers";
import ToolTip from "../../components/component-tools/ToolTip";
import ConfirmPopup from "../../components/client-components/ConfirmPopup";
import { getAllServices } from "../../services/serviceService";
import { Service } from "../../interfaces/service";
import {
  getActiveSubscriptionDetails,
  stopSubscriptionDetail,
} from "../../services/subscriptionDetailService";

const SubscriptionOrders = () => {
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState<Service[]>([]);
  const [subscriptionDetails, setSubscriptionDetails] = useState<
    BiggerSubscriptionDetail[]
  >([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [vendorID, setVendorID] = useState<number>(0);
  const [appliedVendorID, setAppliedVendorID] = useState<number>(0);
  const [showFilter, setShowFilter] = useState(false);
  const [
    showConfirmationStopSubscription,
    setShowConfirmationStopSubscription,
  ] = useState(false);
  const [selectedSubscriptionDetailID, setSelectedSubscriptionDetailID] =
    useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const servicesResponse = await getAllServices();
        setServices(servicesResponse);

        const vendorsResponse = await getVendors();
        const sortedVendors = vendorsResponse.filter((v) => v.vendorID === 4);
        setVendors(sortedVendors);

        const detailsResponse = await getActiveSubscriptionDetails();
        setSubscriptionDetails(detailsResponse);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredDetails = subscriptionDetails
    .filter((detail): detail is NonNullable<typeof detail> => detail !== null)
    .filter(
      (detail) =>
        detail.candidateFirstName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        detail.candidateLastName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
    )
    .filter(
      (detail) => appliedVendorID === 0 || detail.vendorID === appliedVendorID,
    );

  const applyFilter = () => {
    setAppliedVendorID(vendorID);
    setShowFilter(false);
  };

  const resetFilter = () => {
    setVendorID(0);
    setAppliedVendorID(0);
  };

  const handleStopSubscription = async () => {
    if (!selectedSubscriptionDetailID) return;

    setLoading(true);
    setShowConfirmationStopSubscription(false);

    try {
      await stopSubscriptionDetail(selectedSubscriptionDetailID);

      setSubscriptionDetails((prevDetails) =>
        prevDetails.filter(
          (x) => x.subscriptionDetailID !== selectedSubscriptionDetailID,
        ),
      );

      setSuccessMessage("Stopped Subscription.");
    } catch (error) {
      console.error("Error stopping subscription:", error);
      alert("An error occurred while saving the form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <h1 style={{ display: "flex" }}>
          Subscription Orders
          <ToolTip
            tooltipText="All orders that have an active subscription detail. These orders all get invoiced on the 2nd of the month."
            placement="right"
            className="info-icon"
          />
        </h1>

        <div className="page-items-container">
          <button
            className="btn-funnel"
            onClick={() => setShowFilter(!showFilter)}
          >
            <i
              className={`bi bi-funnel-fill filter-icon ${
                appliedVendorID ? "active-filter-icon" : ""
              }`}
            ></i>
          </button>
          <div className="search-container">
            <input
              style={{ border: "none", width: "100%", outline: "none" }}
              type="text"
              placeholder="Search Candidates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm.trim() ? (
              <i
                className="bi bi-x-circle clear-icon"
                onClick={() => setSearchTerm("")}
                style={{ cursor: "pointer" }}
              ></i>
            ) : (
              <i className="bi bi-search search-icon"></i>
            )}
          </div>
        </div>
      </div>

      {/* Filter Popup */}
      {showFilter && (
        <>
          <div
            className="background-mask"
            onClick={() => setShowFilter(false)}
          ></div>
          <div className="filter-popup">
            <div className="close-button-container">
              <button
                className="close-button"
                onClick={() => setShowFilter(false)}
              >
                <i className="bi bi-x-circle"></i>
              </button>
            </div>
            <div className="filter-header">
              <h2>Filter by Vendor</h2>
            </div>
            <div className="filter-body">
              <div className="field-stacked" style={{ flex: "0 0 30%" }}>
                <label className="field-label">Vendor:</label>
                <div className="field-input">
                  <select
                    className="form-control"
                    value={vendorID}
                    onChange={(e) => setVendorID(Number(e.target.value))}
                  >
                    <option value={0}>All Vendors</option>
                    {vendors.map((vendor) => (
                      <option key={vendor.vendorID} value={vendor.vendorID}>
                        {vendor.code}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="filter-actions">
              <button className="smbtn btn-grey" onClick={resetFilter}>
                Reset
              </button>
              <button
                className={`smbtn btn-green ${
                  vendorID === 0 ? "disabled" : ""
                }`}
                onClick={applyFilter}
                disabled={vendorID === 0}
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="table-wrapper">
            <div className="table-container">
              <table
                className="table"
                style={{
                  cursor: "pointer",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: "150px" }}>Candidate Name</th>
                    <th style={{ width: "150px" }}>Service</th>
                    <th style={{ width: "70px" }}>Initiated</th>
                    <th style={{ width: "70px" }}>Vendor</th>
                    <th style={{ width: "50px" }}>Fee</th>
                    <th style={{ width: "100px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDetails?.length === 0 ? (
                    <tr>
                      <td colSpan={6}>No Subscription Orders.</td>
                    </tr>
                  ) : (
                    filteredDetails?.map((detail) => {
                      return (
                        <tr
                          key={detail?.subscriptionDetailID}
                          onClick={() =>
                            navigate(`/admin/view-order/${detail.orderID}`)
                          }
                        >
                          <td>
                            {detail.candidateFirstName}{" "}
                            {detail.candidateLastName}
                          </td>
                          <td>
                            {services.find(
                              (s) => s.serviceID === detail.serviceID,
                            )?.serviceName ?? "Unknown Service"}
                          </td>
                          <td>{formatDate(detail?.initiatedDate, "us")}</td>
                          <td>
                            {vendors.find((v) => v.vendorID === detail.vendorID)
                              ?.code ?? "Unknown Vendor"}
                          </td>
                          <td>{detail?.fee}</td>
                          <td>
                            <div style={{ alignSelf: "end" }}>
                              <button
                                className="smbtn btn-red"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedSubscriptionDetailID(
                                    detail.subscriptionDetailID,
                                  );
                                  setShowConfirmationStopSubscription(true);
                                }}
                              >
                                Stop Subscription
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      {showConfirmationStopSubscription && (
        <ConfirmPopup
          onConfirm={() => {
            handleStopSubscription();
          }}
          onCancel={() => setShowConfirmationStopSubscription(false)}
          message="Confirming will stop this subscription."
        />
      )}
      {successMessage && (
        <div className="alert-message alert alert-success alert-dismissible fade show">
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
    </div>
  );
};

export default SubscriptionOrders;
